import React, { useState } from 'react';

import { useRouter } from 'next/router';

import {
  ButtonGroup,
  Button,
  Drawer,
  IconButton,
  Menu,
  MenuItem,
  Avatar,
  Typography,
} from '@mui/material';

import { AppServicesContext } from '@klokhuis/core/AppServices';
import { AuthContext } from '@klokhuis/core/Auth/AuthContext';
import { useIntl } from '@klokhuis/core/Intl';
import { Locale } from '@klokhuis/core/Intl/types';
import { ROUTES } from '@klokhuis/core/Routing';
import { NavLink } from '@klokhuis/core/Routing/NavLink';

import BurgerIcon from '@klokhuis/icons/Burger.svg';
import CrossIcon from '@klokhuis/icons/Cross.svg';

import styles from './HeaderMobile.module.css';
import { LogoLink } from './LogoLink';

export interface HeaderMobileProps {
  isLandlordPage: boolean;
}

export const HeaderMobile = () => {
  const router = useRouter();
  const { T, locale } = useIntl();
  const { isAuthenticated, userProfile, signout } = AuthContext.useContext();
  const { featureFlags } = AppServicesContext.useContext();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isMenuOpen = Boolean(anchorEl);
  const handleDrawerCloseButtonClick = () => setIsDrawerOpen(false);
  const handleDrawerOpenButtonClick = () => setIsDrawerOpen(true);
  const handleDrawerClose = () => setIsDrawerOpen(false);
  const useIDP = featureFlags.use_idp === 'on';

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleProfileClick = () => {
    handleMenuClose();
    setIsDrawerOpen(false);
    router.push(ROUTES.Profile.url({}));
  };

  const handleLogoutClick = () => {
    handleMenuClose();
    setIsDrawerOpen(false);
    signout();
  };

  return (
    <React.Fragment>
      <nav className={styles.root}>
        <IconButton onClick={handleDrawerOpenButtonClick}>
          <BurgerIcon color="secondary" />
        </IconButton>
        <LogoLink />
        {isAuthenticated && (
          <>
            <div
              id="profile-menu-button"
              aria-label="account menu"
              aria-controls="profile-menu"
              aria-haspopup="true"
              onClick={handleProfileMenuOpen}
              className={styles.userSection}
            >
              <Typography variant="headerNavButton" className={styles.userName}>
                {userProfile?.fullName || ''}
              </Typography>
              <Avatar
                alt={userProfile?.fullName || ''}
                src={userProfile?.profileImage || ''}
                className={styles.avatar}
              />
            </div>
            <Menu
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              id="profile-menu"
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={isMenuOpen}
              onClose={handleMenuClose}
            >
              <MenuItem onClick={handleProfileClick}>{T('header.menu.profile')}</MenuItem>
              <MenuItem onClick={() => router.push(ROUTES.Inbox.url({}))}>
                {T('header.menu.messages')}
              </MenuItem>
              <MenuItem
                onClick={() => router.push(ROUTES.ManageListing.url({}))}
              >
                {T('header.menu.manage_listings')}
              </MenuItem>
              <MenuItem
                onClick={() => {
                  handleMenuClose();
                  setIsDrawerOpen(false);
                  router.push(ROUTES.Syndication.url({}));
                }}
              >
                {T('header.menu.syndication')}
              </MenuItem>
              <MenuItem onClick={handleLogoutClick}>{T('header.menu.logout')}</MenuItem>
            </Menu>
          </>
        )}
      </nav>
      <Drawer open={isDrawerOpen} anchor="left" onClose={handleDrawerClose}>
        <ul className={styles.drawer}>
          <li className={styles.drawerHead}>
            <IconButton onClick={handleDrawerCloseButtonClick}>
              <CrossIcon fontSize="small" color="secondary" />
            </IconButton>
            {/* <LogoLink /> */}
          </li>
          <li>
            <Button
              variant="contained"
              color="primary"
              component={NavLink.Anchor}
              href={
                isAuthenticated
                  ? ROUTES.CreateProperty.url({})
                  : ROUTES.SignupSignin.url({
                      returnUrl: ROUTES.CreateProperty.url({}),
                    })
              }
            >
              <Typography variant="headerNavButton">
                {T('LBL_HEADER_CREATE_ADVERT')}
              </Typography>
            </Button>
          </li>
          {isAuthenticated ? (
            <>
              <li>
                <NavLink.Native
                  href={ROUTES.Profile.url({})}
                  underline="none"
                  onClick={handleDrawerClose}
                >
                  {T('header.menu.profile')}
                </NavLink.Native>
              </li>
              <li>
                <NavLink.Native
                  href={ROUTES.Inbox.url({})}
                  underline="none"
                  onClick={handleDrawerClose}
                >
                  {T('header.menu.messages')}
                </NavLink.Native>
              </li>
              <li>
                <NavLink.Native
                  href={ROUTES.ManageListing.url({})}
                  underline="none"
                  onClick={handleDrawerClose}
                >
                  {T('header.menu.manage_listings')}
                </NavLink.Native>
              </li>
              <li>
                <NavLink.Native
                  href={ROUTES.Syndication.url({})}
                  underline="none"
                  onClick={handleDrawerClose}
                >
                  {T('header.menu.syndication')}
                </NavLink.Native>
              </li>
              <li>
                <NavLink.Native
                  href="#"
                  underline="none"
                  onClick={e => {
                    e.preventDefault();
                    handleDrawerClose();
                    signout();
                  }}
                >
                  {T('header.menu.logout')}
                </NavLink.Native>
              </li>
            </>
          ) : (
            <li>
              <NavLink.Native
                href={ROUTES.SignupSignin.url({})}
                underline="none"
              >
                {T('LBL_LOGIN')}
              </NavLink.Native>
            </li>
          )}
          {/* <li className={styles.drawerDivider} />
          <li>
            <ButtonGroup size="small">
              <Button
                variant={locale === Locale.en ? 'contained' : 'outlined'}
                onClick={() => {
                  router.push(router.asPath, router.asPath, { locale: Locale.en });
                  handleDrawerClose();
                }}
              >
                {T('header.lang_switch.en')}
              </Button>
              <Button
                variant={locale === Locale.ta ? 'contained' : 'outlined'}
                onClick={() => {
                  router.push(router.asPath, router.asPath, { locale: Locale.ta });
                  handleDrawerClose();
                }}
              >
                {T('header.lang_switch.ta')}
              </Button>
            </ButtonGroup>
          </li> */}
        </ul>
      </Drawer>
    </React.Fragment>
  );
};
